import type { FetchNextPageOptions } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useIntersection } from 'react-use';
import { Loader } from 'shared/ui/Loader';
import { formatDecimal } from 'shared/lib/formatting';
import type { CustomMenuProps, Option } from 'shared/ui/MultiSelect/interfaces';
import { getMenuBaseProps } from 'shared/ui/MultiSelect/Multiselect/utils';
import { BaseCheckbox } from 'shared/ui/Checkbox';

import { SearchInput } from './MenuInfiniteSearch';
import { ListOptionComponent } from './ListOption';
import { ListMenu } from './Menu';
import styles from './Menu.module.scss';

export type UpdateOptionsHandlerParams<T> = {
  options: T[];
  prevOptions: T[];
  selectedOptions: T[];
  searchingValue: string;
};

export interface MenuInfiniteProps<T extends Option> extends Pick<CustomMenuProps, 'handleClose'> {
  className?: string;
  placeholder?: string;
  showFullText?: boolean;
  hasNextPage: boolean;
  loading: boolean;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: (params?: FetchNextPageOptions) => void;
  removeQuery: () => void;
  totalItems: number;
  open: boolean;
  options: T[];
  selectedOptions: T[];
  onChangeSearchQuery: (value: string) => void;
  handleSelect: (option: T[], e?: React.MouseEvent<HTMLLIElement>) => void;
  withoutCheckbox?: boolean;
  singleSelect?: boolean;
  updateOptionsHandler?: (params: UpdateOptionsHandlerParams<T>) => T[];
  withResize?: boolean;
  menuDefaultWidth?: number;
}

export function updateOptionsToRender<T extends Option>({
  options,
  prevOptions,
  selectedOptions,
  searchingValue,
}: UpdateOptionsHandlerParams<T>) {
  const prevOptionsMap = prevOptions.reduce((acc, option) => {
    return {
      ...acc,
      [option.id]: option.id,
    };
  }, {} as Record<string, string>);

  const filteredSelectedOptions =
    searchingValue.length > 0
      ? selectedOptions.filter((option) => option.text.toLocaleUpperCase().includes(searchingValue.toLocaleUpperCase()))
      : selectedOptions;

  const newOptions = [...prevOptions, ...filteredSelectedOptions.filter((option) => !prevOptionsMap[option.id])];

  const newOptionsMap = newOptions.reduce((acc, option) => {
    return {
      ...acc,
      [option.id]: option.id,
    };
  }, {} as Record<string, string>);

  return [...newOptions, ...options.filter((option) => !newOptionsMap[option.id])];
}

function MenuInfiniteComponent<T extends Option>({
  className,
  open,
  options,
  selectedOptions,
  handleSelect,
  hasNextPage,
  totalItems,
  loading,
  isFetchingNextPage,
  isFetching,
  removeQuery,
  fetchNextPage,
  onChangeSearchQuery,
  withoutCheckbox,
  singleSelect,
  handleClose,
  updateOptionsHandler = updateOptionsToRender,
  placeholder,
  showFullText,
  menuDefaultWidth,
  withResize,
}: MenuInfiniteProps<T>) {
  const [searchingValue, setSearchingValue] = useState('');
  const [optionsToRender, setOptionsToRender] = useState(options);
  const [isSearchPrinting, setIsSearchPrinting] = useState(false);
  const scrollAnchor = useRef<HTMLLIElement>(null);

  const handleClearOptions = () => {
    setOptionsToRender([]);
  };

  const intersection = useIntersection(scrollAnchor, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });

  const selectedOptionsMap = useMemo(() => {
    return selectedOptions.reduce((acc, option) => {
      return {
        ...acc,
        [option.id]: option.id,
      };
    }, {} as Record<string, string>);
  }, [selectedOptions]);

  const handleSingleSelect = (option: T, e?: React.MouseEvent<HTMLLIElement>) => {
    handleSelect([option], e);
    handleClose();
  };

  const handleMultipleSelect = (option: T, e?: React.MouseEvent<HTMLLIElement>) => {
    const selected = selectedOptions.some((selectedOption) => selectedOption.id === option.id);

    if (selected) {
      handleSelect(
        selectedOptions.filter(({ id }) => id !== option.id),
        e
      );
    } else {
      handleSelect([...selectedOptions, option], e);
    }
  };

  const handleCloseMenu = () => {
    if (selectedOptions.length > 0) {
      setOptionsToRender((prevOptions) => [
        ...selectedOptions,
        ...prevOptions.filter((option) => !selectedOptionsMap[option.id]),
      ]);
    }
  };

  useEffect(() => {
    setOptionsToRender((prevOptions) =>
      updateOptionsHandler({
        options,
        selectedOptions,
        searchingValue,
        prevOptions,
      })
    );
  }, [options, selectedOptions]);

  const showBorder = intersection && intersection.intersectionRatio < 1;
  const isLoading = loading || isFetching || isFetchingNextPage;
  const isEmptyList = !isSearchPrinting && optionsToRender.length === 0;
  const showLoader = isLoading || isSearchPrinting;
  const showMore = hasNextPage && optionsToRender.length < totalItems;

  useEffect(() => {
    if (!isLoading) {
      setIsSearchPrinting(false);
    }
  }, [isLoading]);

  return (
    <ListMenu
      open={open}
      className={className}
      menuDefaultWidth={menuDefaultWidth}
      withResize={withResize}
      inputBlock={
        <>
          {open && (
            <SearchInput
              onChangeSearchQuery={onChangeSearchQuery}
              fetchNextPage={fetchNextPage}
              placeholder={placeholder}
              removeQuery={removeQuery}
              showBorder={!!showBorder}
              handleCloseMenu={handleCloseMenu}
              setSearchingValue={setSearchingValue}
              setIsSearchPrinting={setIsSearchPrinting}
              onClearOptions={handleClearOptions}
            />
          )}
        </>
      }
      controlsBlock={
        <div
          className={classNames('flex border-t px-4 pt-4 text-[14px] font-medium text-slate-600', {
            'justify-end': !isSearchPrinting && !isEmptyList,
            'justify-start': isSearchPrinting || isEmptyList,
            'border-slate-100': !isSearchPrinting && !isEmptyList,
            'border-transparent': isSearchPrinting || isEmptyList,
          })}
        >
          {isSearchPrinting && 'Loading...'}
          {isEmptyList && 'No results'}
          {!isSearchPrinting && !isEmptyList && (
            <>
              {formatDecimal(optionsToRender.length)} of {formatDecimal(totalItems)}
            </>
          )}
        </div>
      }
    >
      {optionsToRender.length > 0 && !isSearchPrinting && (
        <>
          <li ref={scrollAnchor} />
          {optionsToRender.map((option) => {
            const { key, selected } = getMenuBaseProps({ selectedOptions, option });

            return (
              <ListOptionComponent
                selected={selected}
                key={key}
                handleSelect={singleSelect ? handleSingleSelect : handleMultipleSelect}
                option={option}
                className="py-[10px]"
                renderOption={(selected) => (withoutCheckbox ? <></> : <BaseCheckbox isChecked={selected} />)}
                showFullText={showFullText}
              />
            );
          })}
        </>
      )}

      {showLoader && (
        <li className={styles.option}>
          <Loader
            className="flex w-full justify-center"
            svgClasses="w-[15px] h-[15px]"
          />
        </li>
      )}
      {showMore && !showLoader && (
        <li className={styles.option}>
          <button
            className="w-full text-left text-deep-blue-500"
            onClick={() => fetchNextPage()}
          >
            Show more
          </button>
        </li>
      )}
    </ListMenu>
  );
}

export const MenuInfinite = React.memo(MenuInfiniteComponent);
